import { toPng } from 'html-to-image';

function escapeRegExp(value) {
  return `${value}`.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function rewriteMermaidScopedCss(exportRoot) {
  const svgElement = exportRoot?.tagName?.toLowerCase() === 'svg' ? exportRoot : exportRoot.querySelector('svg');
  if (!svgElement) return;

  const svgId = svgElement.getAttribute('id');
  if (!svgId) return;

  // Mermaid suele scopear así: "#mermaid-xxxx .node { ... }"
  // html-to-image puede clonar/ajustar ids, rompiendo el matching y dejando estilos por defecto.
  // Para exportar un único SVG, es seguro quitar el scope por id.
  const styleElements = exportRoot.querySelectorAll('style');
  const idRegex = new RegExp(`#${escapeRegExp(svgId)}(?=\\s|\\.|#|\\[|\\{|:)`, 'g');

  styleElements.forEach((styleElement) => {
    const cssText = styleElement.textContent;
    if (!cssText || !cssText.includes(`#${svgId}`)) return;

    // Reemplazamos "#<id>" por "svg" para mantener el scope pero sin dependencia del id.
    // Ej: "#id .node" -> "svg .node"
    styleElement.textContent = cssText.replace(idRegex, 'svg');
  });
}

function parseClipPathUrl(value) {
  if (!value) return null;
  // Soporta:
  // - url(#clipId)
  // - url(http://host/page#clipId)
  // - url("...#clipId")
  const match = `${value}`.match(/url\((?:["']?)(?:[^#)"']*#)?([^)"']+)(?:["']?)\)/);
  return match?.[1] || null;
}

function stripClipMaskFromStyleAttribute(element) {
  const style = element.getAttribute('style');
  if (!style) return;

  const sanitized = style
    .split(';')
    .map((part) => part.trim())
    .filter((part) => part && !part.startsWith('clip-path:') && !part.startsWith('mask:'))
    .join(';');

  if (!sanitized) {
    element.removeAttribute('style');
    return;
  }

  element.setAttribute('style', `${sanitized}${sanitized.trim().endsWith(';') ? '' : ';'}`);
}

function removeTextClipping(svgElement) {
  // En algunos exports (canvas) la métrica cambia 1-2px y clip-path/mask “muerde” letras,
  // aunque el SVG en pantalla se vea bien. Para export, quitamos clipping cerca del texto.
  const textLikeNodes = svgElement.querySelectorAll('text, tspan, foreignObject');

  textLikeNodes.forEach((node) => {
    let current = node;
    let steps = 0;

    while (current && current !== svgElement && steps < 6) {
      if (current.removeAttribute) {
        current.removeAttribute('clip-path');
        current.removeAttribute('mask');
      }

      if (current.getAttribute) stripClipMaskFromStyleAttribute(current);

      current = current.parentElement;
      steps += 1;
    }
  });
}

function appendStyleDeclaration(element, declaration) {
  const existing = element.getAttribute('style') || '';
  element.setAttribute('style', `${existing}${existing && !existing.trim().endsWith(';') ? ';' : ''}${declaration}`);
}

function widenForeignObjects(svgElement, extraRight = 24) {
  // Si Mermaid usa htmlLabels, el texto vive dentro de <foreignObject> (HTML).
  // En export (canvas) el layout puede medir 1-2px distinto y cortar letras.
  // Ensanchamos el foreignObject un poco y forzamos overflow visible.
  const foreignObjects = svgElement.querySelectorAll('foreignObject');
  if (!foreignObjects.length) return;

  foreignObjects.forEach((fo) => {
    const width = Number.parseFloat(fo.getAttribute('width') || '0');
    if (Number.isFinite(width) && width > 0) {
      const delta = Number.isFinite(extraRight) ? extraRight : 0;
      fo.setAttribute('width', `${width + delta}`);
    }

    appendStyleDeclaration(fo, 'overflow:visible;');

    const innerNodes = fo.querySelectorAll('*');
    innerNodes.forEach((node) => {
      if (!node.getAttribute) return;
      appendStyleDeclaration(node, 'overflow:visible;white-space:nowrap;box-sizing:content-box;padding-right:8px;');
    });
  });
}

function widenMermaidTextClipPaths(svgElement, extraPaddingX = 12) {
  // Mermaid (mindmap) usa clipPath/mask para recortar labels. En export (html-to-image)
  // puede cambiar la métrica 1-2px y cortar letras. Aquí ensanchamos los rects de clipPath.
  const clipPathIds = new Set();
  const clippedElements = svgElement.querySelectorAll('[clip-path]');

  clippedElements.forEach((el) => {
    const id = parseClipPathUrl(el.getAttribute('clip-path'));
    if (id) clipPathIds.add(id);
  });

  clipPathIds.forEach((id) => {
    const clipPath = svgElement.querySelector(`clipPath#${CSS.escape(id)}`);
    if (!clipPath) return;

    const rects = clipPath.querySelectorAll('rect');
    rects.forEach((rect) => {
      const x = Number.parseFloat(rect.getAttribute('x') || '0');
      const y = Number.parseFloat(rect.getAttribute('y') || '0');
      const width = Number.parseFloat(rect.getAttribute('width') || '0');
      const height = Number.parseFloat(rect.getAttribute('height') || '0');

      if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return;

      const deltaX = Number.isFinite(extraPaddingX) ? extraPaddingX : 0;
      const leftPad = deltaX * 0.25; // pequeño margen también a la izquierda

      rect.setAttribute('x', `${x - leftPad}`);
      rect.setAttribute('y', `${y}`);
      rect.setAttribute('width', `${width + deltaX}`);
      rect.setAttribute('height', `${height}`);
    });
  });
}

function getSvgExportBox(svgElement, padding) {
  const pad = Number.isFinite(padding) ? padding : 0;

  try {
    const bbox = svgElement.getBBox();
    // bbox puede tener x/y negativos
    return {
      height: bbox.height + pad * 2,
      viewBox: `${bbox.x - pad} ${bbox.y - pad} ${bbox.width + pad * 2} ${bbox.height + pad * 2}`,
      width: bbox.width + pad * 2,
    };
  } catch {
    const rect = svgElement.getBoundingClientRect();
    const width = (rect.width || 800) + pad * 2;
    const height = (rect.height || 600) + pad * 2;
    return {
      height,
      viewBox: `0 0 ${width} ${height}`,
      width,
    };
  }
}

function triggerPngDownload(dataUrl, filename) {
  // Un canvas por encima del límite del navegador no lanza: devuelve 'data:,'.
  if (!`${dataUrl}`.startsWith('data:image/png')) {
    throw new Error('El navegador no pudo generar la imagen');
  }

  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = `${filename}.png`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Descarga un SVG desde un contenedor que lo contiene
 * Para Mermaid: usa el SVG y ajusta viewBox/size para evitar recortes en export.
 * @param {Element} container - El contenedor que contiene el SVG
 * @param {Object} options - Opciones de configuración
 * @returns {Promise<void>}
 */
export async function downloadSvgFromContainer(container, options = {}) {
  if (!container) {
    throw new Error('No se proporcionó un contenedor válido');
  }

  const { filename = `diagram-${Date.now()}`, scale = 2, backgroundColor = 'white', padding = 20 } = options;

  try {
    const svgElement = container.querySelector('svg');

    // Caso Mermaid: exportar el SVG directamente evita que el contenedor recorte contenido que desborda
    // (típico en mindmap), y nos permite ajustar viewBox/size a partir de getBBox().
    if (svgElement) {
      const exportSvg = svgElement.cloneNode(true);
      rewriteMermaidScopedCss(exportSvg);
      widenMermaidTextClipPaths(exportSvg, 14);
      removeTextClipping(exportSvg);
      widenForeignObjects(exportSvg, 28);

      const { height, viewBox, width } = getSvgExportBox(svgElement, padding);

      exportSvg.setAttribute('viewBox', viewBox);
      exportSvg.setAttribute('width', `${width}`);
      exportSvg.setAttribute('height', `${height}`);
      exportSvg.style.overflow = 'visible';
      exportSvg.style.display = 'block';

      const dataUrl = await toPng(exportSvg, {
        backgroundColor,
        height,
        pixelRatio: scale,
        width,
      });

      triggerPngDownload(dataUrl, filename);
      return;
    }

    const rect = container.getBoundingClientRect();
    const width = rect.width + padding * 2;
    const height = rect.height + padding * 2;

    const config = {
      backgroundColor,
      pixelRatio: scale,
      width,
      height,
      style: {
        padding: `${padding}px`,
        boxSizing: 'border-box',
      },
    };

    const dataUrl = await toPng(container, config);

    triggerPngDownload(dataUrl, filename);
  } catch (error) {
    throw new Error(`Error al descargar la imagen: ${error.message}`);
  }
}
